import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { tokenUrl, loginUrl } from "../../../shared/baseUrl";
import { RootState } from "../../../Redux/store";
import localStorage from "redux-persist/lib/storage";
import { Login } from "../../../Types/Login";
import { normalizePermission, PermissionLevel } from "./permissionNormalizer";

type Credentials = {
    username: string;
    password: string;
}

type LoginPayload = Credentials & {
    rememberMe: boolean;
};

const userToken = "userToken";
const CREDENTIALS_KEY = 'savedCredentials'

export const saveCredentials = async (
    username: string,
    password: string
): Promise<void> => {
    const data = JSON.stringify({ username, password });
    await localStorage.setItem(CREDENTIALS_KEY, data);
}

export const getCredentials = async (): Promise<Credentials | null> => {
    const json = await localStorage.getItem(CREDENTIALS_KEY);
    return json ? JSON.parse(json) : null;
}

export const clearCredentials = async (): Promise<void> => {
    await localStorage.removeItem(CREDENTIALS_KEY);
}

const saveToken = async (key: string, value: string) => {
    await localStorage.setItem(key, value);
};

const getToken = async (key: string): Promise<string | any> => {
    return await localStorage.getItem(key);
};

const deleteToken = async (key: string) => {
    await localStorage.removeItem(key);
};

export const fetchToken = createAsyncThunk(
    "auth/fetchToken",
    async (_, thunkAPI) => {
        try {
            const response = await fetch(tokenUrl, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) {
                return thunkAPI.rejectWithValue("Failed to fetch token");
            }

            const data = await response.json();
            const token = data.token;

            await saveToken(userToken, token);
            return token;
        } catch (err: any) {
            return thunkAPI.rejectWithValue(err.message);
        }
    }
);

export const userLogin = createAsyncThunk<Login, LoginPayload, { rejectValue: string }>(
    "auth/userLogin",
    async ({ username, password, rememberMe }, { rejectWithValue }) => {
        try {
            let token = await getToken(userToken);
            console.log('Token:', token)

            const loginResponse = await fetch(loginUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": btoa(`Bearer ${token}`),
                },
                body: JSON.stringify({ username, password }),
            });

            const json = await loginResponse.json();

            if (!json.ack) {
                await clearCredentials();
                return rejectWithValue("Invalid credentials");
            }

            if (rememberMe) {
                await saveCredentials(username, password)
            } else {
                await clearCredentials();
            }
            return json;
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);

interface AuthState {
    isAuthorized: boolean;
    loading: boolean;
    token: string | null;
    error: any;
    permissions: Record<string, PermissionLevel>
};

const initialState: AuthState = {
    isAuthorized: false,
    loading: false,
    token: null,
    error: null,
    permissions: {},
}

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setToken: (state, action) => {
            state.token = action.payload;
        },
        logoutUser: (state) => {
            state.isAuthorized = false;
            // state.token = null;
            state.loading = false;
            // deleteToken(userToken);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchToken.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchToken.fulfilled, (state, action) => {
                state.loading = false;
                state.token = action.payload;
            })
            .addCase(fetchToken.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(userLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(userLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.isAuthorized = true;

                state.permissions = action.payload.authSettings.reduce(
                    (acc, item) => {
                        acc[item.authName] =
                            normalizePermission(item.authVal);

                        return acc;
                    },
                    {} as Record<string, PermissionLevel>
                );

                state.error = null;
            })
            .addCase(userLogin.rejected, (state, action) => {
                state.loading = false;
                state.isAuthorized = false;
                state.error = action.payload || "Login Failed";
            })
    },
});

export const { setToken, logoutUser } = authSlice.actions;
export const authReducer = authSlice.reducer;
export const selectRole = (state: RootState) => {
    return state.auth.role;
};